import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from './config';
import { useAuth } from './AuthContext';
import Nav from './Nav';
import './css/trust.css';

// GROUPS — the management surface for the middle value of the human access
// axis (2026-08-13; the backend CRUD shipped 2026-08-08 and was reachable only
// by HTTP until this page). A group here is a NAME and a MEMBER LIST, nothing
// else — no roles, no settings, no group content. That is a ruling, not a
// pending feature: groups are a read audience, every mutation everywhere stays
// creator-only, and there is no moderator (DECISIONS.md "Groups are an
// audience, not a role").
//
// The add-member picker searches through the DISCOVERABLE branch of
// GET /api/objects (`object_type=user&q=`) — the same bounded name lookup the
// relation form uses, not a second endpoint.

function AddMember({ groupId, onAdded }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setResults([]);
      return undefined;
    }
    const t = setTimeout(() => {
      apiFetch(`/api/objects?object_type=user&q=${encodeURIComponent(query)}&limit=8`)
        .then((r) => (r.ok ? r.json() : []))
        .then((rows) => setResults(Array.isArray(rows) ? rows.slice(0, 8) : []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const add = async (userObjectId) => {
    setError(null);
    try {
      const res = await apiFetch(`/api/groups/${groupId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_object_id: userObjectId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'failed');
      }
      setQ('');
      setResults([]);
      onAdded();
    } catch (e) {
      setError(e.message || 'Failed to add the member');
    }
  };

  return (
    <div style={{ marginTop: 'var(--wv-sp-2)' }}>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Add a member by username…"
        style={{ minWidth: '220px' }}
      />
      {error && <div style={{ color: 'var(--wv-neg)', fontSize: 'var(--wv-fs-xs)', marginTop: '4px' }}>{error}</div>}
      {results.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--wv-sp-1)', flexWrap: 'wrap', marginTop: 'var(--wv-sp-1)' }}>
          {results.map((u) => (
            <button key={u.id} type="button" className="wv-chip" onClick={() => add(u.id)} style={{ cursor: 'pointer' }}>
              + {u.content}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function GroupCard({ group, myObjectId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState(null);
  const [error, setError] = useState(null);

  const loadMembers = useCallback(() => {
    apiFetch(`/api/groups/${group.id}/members`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('failed'))))
      .then(setMembers)
      .catch(() => setError('Could not load members'));
  }, [group.id]);

  useEffect(() => {
    if (open && members === null) loadMembers();
  }, [open, members, loadMembers]);

  const remove = async (userObjectId) => {
    setError(null);
    try {
      const res = await apiFetch(`/api/groups/${group.id}/members/${userObjectId}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'failed');
      }
      // Removal takes effect on the member's very next request (the backend
      // resolves membership per request, not per login).
      loadMembers();
      onChanged();
    } catch (e) {
      setError(e.message || 'Failed to remove');
    }
  };

  const amMember = members?.some((m) => m.user_object_id === myObjectId);

  return (
    <div className="wv-card" style={{ marginBottom: 'var(--wv-sp-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--wv-sp-2)', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 'var(--wv-fs-md)' }}>{group.name}</strong>
        {group.owned && <span className="wv-chip">yours</span>}
        <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>
          {group.member_count} member{group.member_count === 1 ? '' : 's'}
        </span>
        <button type="button" onClick={() => setOpen((o) => !o)} style={{ fontSize: 'var(--wv-fs-xs)', marginLeft: 'auto' }}>
          {open ? 'close' : 'members…'}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 'var(--wv-sp-2)' }}>
          {error && <div style={{ color: 'var(--wv-neg)', fontSize: 'var(--wv-fs-xs)' }}>{error}</div>}
          {members === null && !error && (
            <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>Loading…</span>
          )}
          {members?.map((m) => (
            <div key={m.user_object_id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--wv-sp-2)', padding: '3px 0' }}>
              <span style={{ fontSize: 'var(--wv-fs-sm)' }}>{m.name}</span>
              {m.user_object_id === myObjectId && (
                <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>(you)</span>
              )}
              {(group.owned || m.user_object_id === myObjectId) && (
                <button
                  type="button"
                  onClick={() => remove(m.user_object_id)}
                  style={{ fontSize: 'var(--wv-fs-xs)', marginLeft: 'auto', color: 'var(--wv-neg)' }}
                >
                  {m.user_object_id === myObjectId ? 'leave' : 'remove'}
                </button>
              )}
            </div>
          ))}
          {group.owned && <AddMember groupId={group.id} onAdded={() => { loadMembers(); onChanged(); }} />}
          {!group.owned && amMember && (
            <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-1)' }}>
              Only the owner adds members; leaving is always yours to do.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Groups() {
  const { user } = useAuth();
  const [groups, setGroups] = useState(null);
  const [name, setName] = useState('');
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    apiFetch('/api/groups')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('failed'))))
      .then((g) => setGroups(Array.isArray(g) ? g : []))
      .catch(() => setError('Could not load your groups'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const res = await apiFetch('/api/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'failed');
      }
      setName('');
      load();
    } catch (e2) {
      setError(e2.message || 'Failed to create the group');
    }
  };

  return (
    <div className="trust-page">
      <Nav />
      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 20px 40px' }}>
        <h1 style={{ marginTop: 0 }}>Groups</h1>
        <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', lineHeight: 1.55 }}>
          A group is an audience: a name and a member list. Content shared with a group is readable by its
          members — nothing more. Removal takes effect on the member’s next request.
        </p>

        {error && <div style={{ color: 'var(--wv-neg)', marginBottom: '10px' }}>{error}</div>}

        <form onSubmit={create} style={{ display: 'flex', gap: 'var(--wv-sp-2)', marginBottom: 'var(--wv-sp-3)', flexWrap: 'wrap' }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="New group name…"
            style={{ flex: 1, minWidth: '220px' }}
          />
          <button type="submit" disabled={!name.trim()}>Create group</button>
        </form>

        {groups === null && !error && <p className="trust-muted">Loading…</p>}
        {groups?.length === 0 && (
          <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>
            No groups yet. Create one above, then pick “A group” when you write or share something.
          </p>
        )}
        {groups?.map((g) => (
          <GroupCard key={g.id} group={g} myObjectId={user?.object_id} onChanged={load} />
        ))}
      </div>
    </div>
  );
}
